/**
 * `useResourceCopyMove` — the SPA-side wire to the runtime's cross-scope
 * resource `document.copy` / `document.move` gateway RPCs (CCM Wave 3, R4 —
 * lexruntime `ce359f0f`).
 *
 * Closes the frontend of
 * `ai-docs/handoffs/ccm-lexide-copy-move-ui-handoff-2026-06-30.md`:
 *
 *   1. Builds the `{from, to}` envelope — `from` carries the source scope +
 *      the resource path; `to` is the destination scope address only.
 *   2. Calls `rpc.documentCopy` / `rpc.documentMove` by `mode`.
 *   3. On success, invalidates the DESTINATION scope's `case-workspace-tree`
 *      query (`["case-workspace-tree", <dest key segment>]`, keyed exactly
 *      like `useCaseWorkspaceTree` via `scopeKeySegment` / `targetKeySegment`)
 *      so the dest tree refreshes; for a MOVE, ALSO invalidates the SOURCE
 *      scope's tree (the resource left it).
 *
 * Returns the `useMutation` result; callers should:
 *   - `mutation.mutate(input)` to fire the op
 *   - read `mutation.isPending` for the spinner
 *   - read `mutation.error` for the hard-error toast (the picker surfaces it)
 *
 * The toast ("Copied to <dest>" / "Moved to <dest>") is owned by the picker
 * component (project pattern: components own i18n + `useToastStore`), which
 * has the localized dest label in hand.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useOpBlockStore } from "@/stores/useOpBlockStore";
import {
  targetKeySegment,
  sourceKeySegment,
  type CopyMoveTarget,
  type SourceScope,
} from "@/lib/copy-move-targets";
import type {
  DocumentCopyMoveParams,
  DocumentCopyMoveResult,
} from "@/types/gateway";

/** Input for the copy/move mutation. The source SCOPE + the resource `path`
 *  come from the active selection + the clicked file row; the destination
 *  `target` comes from the picker. For a CASE source we additionally need the
 *  full parent ids to build the runtime `from` address — they're carried on
 *  `source` (the active selection supplies them). */
export interface ResourceCopyMoveInput {
  mode: "copy" | "move";
  /** The resource being copied/moved, workspace-relative (e.g.
   *  "sources/contrato.pdf"). */
  path: string;
  /** The destination scope (from the picker). */
  target: CopyMoveTarget;
  /** The source scope. For a case source the full ids are required (a case
   *  `from` address needs the triple); for org only `{scope:"org"}`. */
  source:
    | { scope: "case"; clientId: string; matterId: string; caseId: string }
    | { scope: "org" };
}

/** Thrown when the gateway isn't connected — surfaced with a stable code so
 *  the picker can translate it (no hardcoded strings in the hook). */
export class ResourceCopyMoveError extends Error {
  readonly code: "noGateway";
  constructor(code: "noGateway") {
    super(code);
    this.code = code;
  }
}

/** The source SCOPE in the `SourceScope` shape (caseId or org) — drives the
 *  invalidation key + the exclude-current-scope target build upstream. */
function sourceScopeOf(source: ResourceCopyMoveInput["source"]): SourceScope {
  return source.scope === "org"
    ? { scope: "org" }
    : { scope: "case", caseId: source.caseId };
}

export function useResourceCopyMove() {
  const rpc = useGatewayStore((s) => s.rpc);
  const queryClient = useQueryClient();
  const beginBlock = useOpBlockStore((s) => s.begin);
  const endBlock = useOpBlockStore((s) => s.end);

  return useMutation<DocumentCopyMoveResult, Error, ResourceCopyMoveInput>({
    // `always` — never let React Query PAUSE the mutation when the browser
    // reports offline (default 'online' fires onMutate → beginBlock but then
    // pauses BEFORE mutationFn, so onSettled/endBlock never run and the block
    // overlay would wedge the IDE indefinitely). Here mutationFn's own
    // `if (!rpc) throw noGateway` fails fast when disconnected → onSettled →
    // endBlock + an error toast, instead of an unbounded block.
    networkMode: "always",
    mutationFn: async ({ mode, path, target, source }) => {
      if (!rpc) throw new ResourceCopyMoveError("noGateway");

      // `from` REQUIRES a path; `to` is the destination scope address ONLY.
      const from: DocumentCopyMoveParams["from"] =
        source.scope === "org"
          ? { scope: "org", path }
          : {
              scope: "case",
              clientId: source.clientId,
              matterId: source.matterId,
              caseId: source.caseId,
              path,
            };
      const params: DocumentCopyMoveParams = { from, to: target.address };
      return mode === "copy" ? rpc.documentCopy(params) : rpc.documentMove(params);
    },
    // Block the whole IDE while the op runs — these carry the sidecar set +
    // reindex, and the fast paths (send-to, drag) have no in-flight surface
    // (operator brief 2026-07-03). Kept up until the tree refetch settles too
    // (below), so the fresh tree is visible the moment the block lifts.
    onMutate: (vars) => {
      beginBlock(vars.mode);
    },
    onSuccess: async (_data, vars) => {
      // Refresh the DESTINATION tree — the dest key segment mirrors
      // `useCaseWorkspaceTree`'s `scopeKeySegment` ("org" or the dest caseId).
      // AWAIT so the refetch completes inside the blocking window (the op isn't
      // "done" until the workspace tree reflects it).
      await queryClient.invalidateQueries({
        queryKey: ["case-workspace-tree", targetKeySegment(vars.target)],
      });
      // A MOVE removed the resource from the source — refresh that tree too.
      if (vars.mode === "move") {
        await queryClient.invalidateQueries({
          queryKey: [
            "case-workspace-tree",
            sourceKeySegment(sourceScopeOf(vars.source)),
          ],
        });
      }
    },
    onSettled: () => {
      endBlock();
    },
  });
}
